import { GameCategory, UserAchievementsType } from '../enums';

export const PUBLIC_SIGNAL_PROFILE_VIEWS = 100;

export const ROOKIE_MATCHES_THRESHOLD = 30;
export const ALL_STAR_MATCHES_THRESHOLD = 60;
export const SUPERSTAR_MATCHES_THRESHOLD = 90;

export const TENNIS_ROOKIE_MATCHES_THRESHOLD = 55;
export const TENNIS_ALL_STAR_MATCHES_THRESHOLD = 110;
export const TENNIS_SUPERSTAR_MATCHES_THRESHOLD = 165;

export const LOCKED_IN_PREDICTIONS_LIMIT = 10;
export const BASELINE_CONTRIBUTOR_TOP_USERS = 100;
export const ONE_PERCENT_CLUB_TOP_USERS = 100;

export const CENTER_COURT_TOP_PERCENT = 25;
export const CENTER_COURT_MULTIPLIER = 3;

export const MATCH_ROUND_TENNIS_MATCHES: Record<string, number> = {
	'First Round': 64,
	'Second Round': 64,
	'Third Round': 32,
	'Round of 16': 16,
	Quarterfinals: 8,
	Semifinals: 4,
	Finals: 2,
};

export const HUNDRED_TRADES_COUNT = 100;
export const TWO_HUNDRED_TRADES_COUNT = 250;
export const THOUSAND_TRADES_COUNT = 1000;
export const SLATE_WINNER_TRADES_COUNT = 25;
export const GOOD_FRIEND_REFERRAL_TRADES = 25;
export const LINKED_AVATAR_BORDER_REFERRALS = 3;
export const COMMUNITY_BUILDER_REFERRALS = 5;

export const MATCH_RANKING_AWARD_DELAY_MS = 4000;
export const MATCH_RANKING_TOP_PERCENT: Record<string, number> = {
	[UserAchievementsType.PlatinumElite]: 0.01,
	[UserAchievementsType.GoldStar]: 0.05,
	[UserAchievementsType.SilverPro]: 0.1,
	[UserAchievementsType.BronzeInitiate]: 0.25,
};

const PLATINUM_ELITE_TOP_PERCENT =
	MATCH_RANKING_TOP_PERCENT[UserAchievementsType.PlatinumElite] * 100;
const GOLD_STAR_TOP_PERCENT = MATCH_RANKING_TOP_PERCENT[UserAchievementsType.GoldStar] * 100;
const SILVER_PRO_TOP_PERCENT = MATCH_RANKING_TOP_PERCENT[UserAchievementsType.SilverPro] * 100;
const BRONZE_INITIATE_TOP_PERCENT =
	MATCH_RANKING_TOP_PERCENT[UserAchievementsType.BronzeInitiate] * 100;

export interface AchievementShareParams {
	userName?: string;
	matchName?: string;
	botName?: string;
}

export interface AchievementMetadata {
	title: string;
	message: string | ((botName?: string) => string);
	profileTitle?: string;
	followUpMessage?: string;
	shareDescription?: string | ((params: AchievementShareParams) => string);
	ogDescription?: string | ((params: AchievementShareParams) => string);
	ogDescriptionNodes?: (params: AchievementShareParams) => unknown[];
	iconSlug?: string;
	pushMessage?: string;
}

export interface MatchCountThresholds {
	rookie: number;
	allStar: number;
	superstar: number;
}

const FIFA_ACHIEVEMENT_TYPES: UserAchievementsType[] = [
	UserAchievementsType.TheKickOff,
	UserAchievementsType.LockedIn,
	UserAchievementsType.Rookie,
	UserAchievementsType.NationalPride,
	UserAchievementsType.MatchChampion,
	UserAchievementsType.PlatinumElite,
	UserAchievementsType.GoldStar,
	UserAchievementsType.SilverPro,
	UserAchievementsType.BronzeInitiate,
	UserAchievementsType.BaselineContributor,
	UserAchievementsType.AlwaysLocked,
	UserAchievementsType.AllStar,
	UserAchievementsType.Superstar,
	UserAchievementsType.PublicSignal,
	UserAchievementsType.TheOnePercentClub,
];

const TENNIS_ACHIEVEMENT_TYPES: UserAchievementsType[] = [
	UserAchievementsType.FirstServe,
	UserAchievementsType.CenterCourt,
	UserAchievementsType.TheWholeDraw,
	UserAchievementsType.AlwaysLocked,
	UserAchievementsType.Rookie,
	UserAchievementsType.AllStar,
	UserAchievementsType.Superstar,
	UserAchievementsType.BaselineContributor,
];

export function getProbabilityTypesForCategory(categoryId?: string | null): UserAchievementsType[] {
	if (categoryId === GameCategory.FIFA) return FIFA_ACHIEVEMENT_TYPES;
	if (categoryId === GameCategory.TENNIS) return TENNIS_ACHIEVEMENT_TYPES;
	return [];
}

export const PROBABILITY_ACHIEVEMENT_TYPES = Array.from(
	new Set([...FIFA_ACHIEVEMENT_TYPES, ...TENNIS_ACHIEVEMENT_TYPES])
);

const MATCH_COUNT_THRESHOLDS: Partial<Record<GameCategory, MatchCountThresholds>> = {
	[GameCategory.FIFA]: {
		rookie: ROOKIE_MATCHES_THRESHOLD,
		allStar: ALL_STAR_MATCHES_THRESHOLD,
		superstar: SUPERSTAR_MATCHES_THRESHOLD,
	},
	[GameCategory.TENNIS]: {
		rookie: TENNIS_ROOKIE_MATCHES_THRESHOLD,
		allStar: TENNIS_ALL_STAR_MATCHES_THRESHOLD,
		superstar: TENNIS_SUPERSTAR_MATCHES_THRESHOLD,
	},
};

export function getMatchCountThresholds(categoryId?: string | null): MatchCountThresholds | null {
	return categoryId ? (MATCH_COUNT_THRESHOLDS[categoryId as GameCategory] ?? null) : null;
}

const TENNIS_ACHIEVEMENT_SET_KEY = 'us-open-2026';

const ACHIEVEMENT_CATEGORY_METADATA_OVERRIDES: Partial<
	Record<GameCategory, Partial<Record<UserAchievementsType, Partial<AchievementMetadata>>>>
> = {
	[GameCategory.TENNIS]: {
		[UserAchievementsType.Rookie]: createRookieMetadata(TENNIS_ROOKIE_MATCHES_THRESHOLD),
		[UserAchievementsType.AllStar]: createAllStarMetadata(TENNIS_ALL_STAR_MATCHES_THRESHOLD),
		[UserAchievementsType.Superstar]: createSuperstarMetadata(TENNIS_SUPERSTAR_MATCHES_THRESHOLD),
		[UserAchievementsType.AlwaysLocked]: {
			pushMessage:
				'Perfect daily attendance! You didn’t miss a single match ever since joining. ⏱️',
			message: (botName?: string) =>
				`${botName ? `${botName} submitted ` : 'Submitted'} a forecast on every match within a single round`,
			ogDescription: ({ userName, botName }: AchievementShareParams) =>
				`Perfect daily attendance. ${botName ?? userName ?? 'A player'} consistently placed forecasts every single day.`,
			shareDescription: ({ botName }: AchievementShareParams) =>
				`${botName ? `${botName} didn’t` : 'Didn’t'} miss a single day forecasting. Consistency beats luck.\n\nStart showing up here:`,
		},
	},
};

export function isAchievementSetKeyValidForCategory(
	categoryId: string | null | undefined,
	achievementSetKey: string
): boolean {
	return categoryId === GameCategory.TENNIS && achievementSetKey === TENNIS_ACHIEVEMENT_SET_KEY;
}

const ICON_SLUG_OVERRIDES: Partial<Record<UserAchievementsType, string>> = {
	[UserAchievementsType.TheOnePercentClub]: '1-percent-club',
};

function resolveIconSlug(type: UserAchievementsType): string | undefined {
	if (!PROBABILITY_ACHIEVEMENT_TYPES.includes(type)) return undefined;
	return ICON_SLUG_OVERRIDES[type] ?? type;
}

function resolveTextField(
	field: string | ((params: AchievementShareParams) => string) | undefined,
	params: AchievementShareParams
): string | undefined {
	if (!field) return undefined;
	if (typeof field === 'function') return field(params);
	return field;
}

export function resolveShareDescription(
	meta: AchievementMetadata | undefined,
	params: AchievementShareParams = {}
): string | undefined {
	return resolveTextField(meta?.shareDescription, params);
}

export function resolveAchievementMessage(
	meta: AchievementMetadata | undefined,
	botName?: string
): string {
	if (!meta) return '';
	return typeof meta.message === 'function' ? meta.message(botName) : meta.message;
}

function createRookieMetadata(threshold: number): AchievementMetadata {
	return {
		title: 'Rookie',
		pushMessage: `${threshold} matches deep. You're officially warmed up! 📊`,
		message: (botName?: string) =>
			`${botName ? `${botName} p` : 'P'}articipated in ${threshold} matches`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${threshold} matches down. ${botName ?? userName ?? 'A player'} is officially calibrated, and climbing the ranks.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${threshold} matches down and ${botName ? `my bot ${botName} is` : "I'm"} still warming up!\n\nStart building your portfolio here:`,
	};
}

function createAllStarMetadata(threshold: number): AchievementMetadata {
	return {
		title: 'All-Star',
		pushMessage: `${threshold} predictions logged. Your consistency is showing! 💪`,
		message: (botName?: string) =>
			`${botName ? `${botName} p` : 'P'}articipated in ${threshold} matches`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${threshold} matches logged. ${botName ?? userName ?? 'A player'} has demonstrated an unyielding spirit.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `${botName} is` : "I'm"} ${threshold} matches deep and running sharp analytics.\n\nCan you keep up?`,
	};
}

function createSuperstarMetadata(threshold: number): AchievementMetadata {
	return {
		title: 'Superstar',
		pushMessage: `${threshold} matches deep! Your relentless dedication is legendary. 🏆`,
		message: (botName?: string) =>
			`${botName ? `${botName} p` : 'P'}articipated in ${threshold} matches`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${threshold} matches deep. ${botName ?? userName ?? 'A player'} has attained peak forecasting endurance.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${threshold} matches deep! ${botName ? `${botName}` : 'I'} just hit an epic commitment milestone.\n\nSee the history: `,
	};
}

export const ACHIEVEMENT_METADATA: Partial<Record<UserAchievementsType, AchievementMetadata>> = {
	[UserAchievementsType.HundredTrades]: {
		title: 'Apprentice',
		profileTitle: `Completed ${HUNDRED_TRADES_COUNT} Trades`,
		message: `You've made your mark with ${HUNDRED_TRADES_COUNT} trades completed.`,
		followUpMessage: 'Keep up the climb!',
	},
	[UserAchievementsType.TwoHundredTrades]: {
		title: 'Rising Predictor',
		profileTitle: `Completed ${TWO_HUNDRED_TRADES_COUNT} Trades`,
		message: `${TWO_HUNDRED_TRADES_COUNT} trades completed!`,
		followUpMessage: 'Your dedication is shining through.',
	},
	[UserAchievementsType.ThousandTrades]: {
		title: 'Market Meister',
		profileTitle: `Completed ${THOUSAND_TRADES_COUNT} Trades`,
		message: 'A thousand predictions completed.',
		followUpMessage: 'Welcome to the big leagues!',
	},
	[UserAchievementsType.SlateWinner]: {
		title: 'Market Sweep',
		profileTitle: '',
		message: `You've perfectly predicted all markets in a single event. ${SLATE_WINNER_TRADES_COUNT} out of ${SLATE_WINNER_TRADES_COUNT} correct trades in`,
		followUpMessage: '',
	},
	[UserAchievementsType.GoodFriend]: {
		title: 'Good Friend',
		profileTitle: `Placed ${GOOD_FRIEND_REFERRAL_TRADES} Trades Thru Referral Link`,
		message: `Invited a new friend that signed up and placed ${GOOD_FRIEND_REFERRAL_TRADES} trades!`,
		followUpMessage: '',
	},
	[UserAchievementsType.LinkedAvatarBorder]: {
		title: 'Linked',
		profileTitle: `Referred ${LINKED_AVATAR_BORDER_REFERRALS} Friends`,
		message: `Successfully invited ${LINKED_AVATAR_BORDER_REFERRALS} friends who completed their ${GOOD_FRIEND_REFERRAL_TRADES} placement trades!`,
		followUpMessage: '',
	},
	[UserAchievementsType.CommunityBuilderBadge]: {
		title: 'Community Builder',
		profileTitle: `Referred ${COMMUNITY_BUILDER_REFERRALS} Friends`,
		message: `The community grows bigger as you've invited a total of ${COMMUNITY_BUILDER_REFERRALS} friends to play!`,
		followUpMessage: '',
	},
	[UserAchievementsType.TheKickOff]: {
		title: 'The Kick-Off',
		pushMessage: 'First forecast locked! Your World Cup journey officially starts here. 🚀',
		message: (botName?: string) =>
			`${botName ? `Your bot ${botName} l` : 'L'}ocked in ${botName ? 'their' : 'the'} very first forecast of the tournament${botName ? '!' : ''}`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${botName ?? userName ?? 'A player'} successfully locked in ${botName ? 'its' : 'their'} very first forecast of the entire tournament.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName} just` : '\nJust'} locked in ${botName ? 'its' : 'my'} very first World Cup forecast on SportsPredict.\n\nClimb the global leaderboard with ${botName ? 'us' : 'me'}!`,
	},
	[UserAchievementsType.FirstServe]: {
		title: 'First Serve',
		pushMessage: 'First forecast locked! Your World Cup journey officially starts here. 🚀',
		message: (botName?: string) =>
			`${botName ? `${botName} l` : 'L'}ocked in the very first forecast of the entire challenge`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${botName ?? userName ?? 'A player'} successfully locked in their very first forecast of the entire challenge.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `${botName} just` : '\nJust'} locked in ${botName ? 'its' : 'my'} very first World Cup forecast on SportsPredict.\n\nClimb the global leaderboard with me!`,
	},
	[UserAchievementsType.LockedIn]: {
		title: 'Locked In',
		pushMessage: `Perfect ${LOCKED_IN_PREDICTIONS_LIMIT}/${LOCKED_IN_PREDICTIONS_LIMIT} slate submitted. Your forecasts are locked and loaded! 🎯`,
		message: (botName?: string) =>
			botName
				? `Your bot ${botName} successfully cleared a full slate of ${LOCKED_IN_PREDICTIONS_LIMIT}/${LOCKED_IN_PREDICTIONS_LIMIT} predictions!`
				: `Awarded for the first time a user successfully submits ${LOCKED_IN_PREDICTIONS_LIMIT} forecasts`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${LOCKED_IN_PREDICTIONS_LIMIT}/${LOCKED_IN_PREDICTIONS_LIMIT} forecasts submitted. Probability lineup cleared by ${botName ?? userName ?? 'a player'}.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName} l` : '\nL'}ocked in ${LOCKED_IN_PREDICTIONS_LIMIT} out of ${LOCKED_IN_PREDICTIONS_LIMIT} predictions before match kickoff.\n\nTry and beat ${botName ? 'its' : 'my'} efficiency:`,
	},
	[UserAchievementsType.Rookie]: createRookieMetadata(ROOKIE_MATCHES_THRESHOLD),
	[UserAchievementsType.AllStar]: createAllStarMetadata(ALL_STAR_MATCHES_THRESHOLD),
	[UserAchievementsType.Superstar]: createSuperstarMetadata(SUPERSTAR_MATCHES_THRESHOLD),
	[UserAchievementsType.NationalPride]: {
		title: 'National Pride',
		pushMessage: "Home turf guarded! You backed your country's colors on the world stage. 🦅",
		message: (botName?: string) =>
			botName
				? `${botName} adjusted its sliders to support your home country on the grand stage.`
				: "Predicted on a match involving the user's home country",
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${botName ?? userName ?? 'A player'} proudly backed ${botName ? 'its' : 'their'} nation's colors on the grand stage.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName} ` : '\nShowed up and '}placed forecasts with my home nation on the cards!\n\nTurn up for your home country:`,
	},
	[UserAchievementsType.MatchChampion]: {
		title: 'Match Champion',
		pushMessage: 'Absolute dominance! You beat the entire field to claim 1st place. 🏆',
		message: (botName?: string) =>
			`${botName ? `Your bot ${botName} beat` : 'Beat'} the entire field to take 1st place in a match!`,
		ogDescription: ({ userName, botName, matchName }: AchievementShareParams) =>
			`${botName ?? userName ?? 'A player'}'s forecasts ranked #1 out of the entire field for the ${matchName ?? 'match'} match.`,
		ogDescriptionNodes: ({ userName, botName, matchName }: AchievementShareParams) => [
			`${botName ?? userName ?? 'A player'}'s forecasts ranked #1 out of the entire field for the `,
			{
				type: 'span',
				props: { style: { fontWeight: 700 }, children: matchName ?? 'match' },
			},
			' match.',
		],
		shareDescription: ({ botName, matchName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName}'s` : 'Not to brag, but my'} predictions just topped the leaderboard for ${matchName ?? 'the match'}.\n\nTake a look:`,
	},
	[UserAchievementsType.PlatinumElite]: {
		title: 'Platinum Elite',
		pushMessage: `Statistically superior. You placed in the elite Top ${PLATINUM_ELITE_TOP_PERCENT}% of the field. 💎`,
		message: (botName?: string) =>
			botName
				? `${botName} finished in the absolute elite top ${PLATINUM_ELITE_TOP_PERCENT}% of the match!`
				: `Joined the absolute elite in the top ${PLATINUM_ELITE_TOP_PERCENT}% of a match.`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`Top ${PLATINUM_ELITE_TOP_PERCENT}% finished. ${botName ?? userName ?? 'A player'} joined the absolute elite of the field.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName} just` : '\nJust'} placed in the Top ${PLATINUM_ELITE_TOP_PERCENT}% of the entire field for this match! No one could match ${botName ? 'it' : 'me'}.\n\nCan you?`,
	},
	[UserAchievementsType.GoldStar]: {
		title: 'Gold Star',
		pushMessage: `Outshined the crowd! You just secured an elite top ${GOLD_STAR_TOP_PERCENT}% match rank. ⭐`,
		message: (botName?: string) =>
			botName
				? `${botName} outpaced the crowd to rank in the top ${GOLD_STAR_TOP_PERCENT}% in a match.`
				: `Outshined the crowd to rank top ${GOLD_STAR_TOP_PERCENT}% in a match.`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`Top ${GOLD_STAR_TOP_PERCENT}% finished. ${botName ?? userName ?? 'A player'} completely outshined the crowd consensus.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName} finished` : '\nFinished'} in the Top ${GOLD_STAR_TOP_PERCENT}% of the match pool. Can you calibrate your forecasts better${botName ? '' : ' than I do'}?\n\nProve it:`,
	},
	[UserAchievementsType.SilverPro]: {
		title: 'Silver Pro',
		pushMessage: `Highly calibrated skill. You fought your way into the Top ${SILVER_PRO_TOP_PERCENT}%! 🥈`,
		message: (botName?: string) =>
			botName
				? `Your bot ${botName} navigated the crowd volatility to rank in the top ${SILVER_PRO_TOP_PERCENT}%.`
				: `Proven your skill to reach top ${SILVER_PRO_TOP_PERCENT}% in a match.`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`Top ${SILVER_PRO_TOP_PERCENT}% finished. ${botName ?? userName ?? 'A player'} showed professional forecasting prowess.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName} walked` : '\nWalked'} away with a Top ${SILVER_PRO_TOP_PERCENT}% finish. I don't see anyone coming as close as that.\n\nProve me wrong:`,
	},
	[UserAchievementsType.BronzeInitiate]: {
		title: 'Bronze Initiate',
		pushMessage: `Comfortably in the green. You finished strong in the Top ${BRONZE_INITIATE_TOP_PERCENT}%. 🥉`,
		message: (botName?: string) =>
			`${botName ? `${botName} finished` : 'Finished'} strong in the top ${BRONZE_INITIATE_TOP_PERCENT}% of a single match.`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`Top ${BRONZE_INITIATE_TOP_PERCENT}% finished. ${botName ?? userName ?? 'A player'} comfortably outpaced the crowd baseline.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName} finished` : '\nFinished'} strong in the Top ${BRONZE_INITIATE_TOP_PERCENT}% of the entire match. The climb continues.\n\nCome and climb with me:`,
	},
	[UserAchievementsType.BaselineContributor]: {
		title: 'Baseline Contributor',
		pushMessage: 'Fast as lightning! You were the first to predict in a match. ⚡',
		message: (botName?: string) =>
			`${botName ? `${botName} f` : 'F'}orecasted as one of the first ${BASELINE_CONTRIBUTOR_TOP_USERS} users in a single match`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`Early bird gets the worm. ${botName ?? userName ?? 'A player'} set the first ever forecast for a match.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? botName : 'I'} was the first to place an entry on a match.\n\nCan you beat me? Play here:`,
	},
	[UserAchievementsType.AlwaysLocked]: {
		title: 'Always Locked',
		pushMessage: "Perfect daily attendance! You didn't miss a single match ever since joining. ⚽",
		message: (botName?: string) =>
			botName
				? `Flawless daily attendance and forecast entries from ${botName}!`
				: 'Submit 10/10 forecasts for every match in a single day block.',
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`Perfect daily attendance. ${botName ?? userName ?? 'A player'} consistently placed forecasts every single day.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName} didn't` : "\nDidn't"} miss a single day forecasting. Consistency beats luck.\n\nStart showing up here:`,
	},

	[UserAchievementsType.PublicSignal]: {
		title: 'Public Signal',
		pushMessage: `${PUBLIC_SIGNAL_PROFILE_VIEWS}+ views! The global crowd is actively studying your strategy. 👀`,
		message: (botName?: string) =>
			botName
				? `${botName}'s prediction algorithm has attracted ${PUBLIC_SIGNAL_PROFILE_VIEWS}+ views on its profile!`
				: `Accumulate ${PUBLIC_SIGNAL_PROFILE_VIEWS}+ profile views on the global leaderboard.`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${PUBLIC_SIGNAL_PROFILE_VIEWS}+ Profile Views. The crowd is actively studying ${botName ?? userName ?? 'A player'}'s moves!`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName}'s` : '\nMy'} profile just crossed ${PUBLIC_SIGNAL_PROFILE_VIEWS}+ views on the global leaderboard.\n\nCheck out the hype:`,
	},
	[UserAchievementsType.TheOnePercentClub]: {
		title: 'World Class',
		pushMessage: `You just broke into the Global Top ${ONE_PERCENT_CLUB_TOP_USERS}. 🥂`,
		message: (botName?: string) =>
			`${botName ? `Your bot ${botName} just` : 'Just'} broke into the Top ${ONE_PERCENT_CLUB_TOP_USERS} of the Global Leaderboard!`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${botName ?? userName ?? 'A player'} has officially broken into the Top ${ONE_PERCENT_CLUB_TOP_USERS} on the Global Leaderboard!`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `My bot ${botName} just` : '\nJust'} broke into the Top ${ONE_PERCENT_CLUB_TOP_USERS} globally on the SportsPredict leaderboard.\n\nCan you knock ${botName ? 'it' : 'me'} off ${botName ? 'its' : 'my'} spot?`,
	},
	[UserAchievementsType.CenterCourt]: {
		title: 'Center Court',
		pushMessage: `Center stage performance! You finished Top ${CENTER_COURT_TOP_PERCENT}% in a ${CENTER_COURT_MULTIPLIER}x multiplier round. 🎾`,
		message: (botName?: string) =>
			`${botName ? `${botName} finished` : 'Finished'} in the top ${CENTER_COURT_TOP_PERCENT}% during a high-stakes ${CENTER_COURT_MULTIPLIER}x multiplier round.`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${botName ?? userName ?? 'A player'} delivered top ${CENTER_COURT_TOP_PERCENT}% forecasting accuracy during a ${CENTER_COURT_MULTIPLIER}x multiplier round.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `${botName} clutched` : 'Clutched'} on Center Court! Placed in the Top ${CENTER_COURT_TOP_PERCENT}% during a ${CENTER_COURT_MULTIPLIER}x multiplier round.\n\nCheck out ${botName ? 'its' : 'my'} receipt:`,
	},
	[UserAchievementsType.TheWholeDraw]: {
		title: 'The Whole Draw',
		pushMessage: 'Full tournament coverage! You forecasted every single round of the draw. 🎾',
		message: (botName?: string) =>
			`${botName ? `${botName} submitted` : 'Submitted'} forecasted every single round of the entire tournament draw.`,
		ogDescription: ({ userName, botName }: AchievementShareParams) =>
			`${botName ?? userName ?? 'A player'} covered the entire challenge from Round 1 to the Final.`,
		shareDescription: ({ botName }: AchievementShareParams) =>
			`${botName ? `${botName} submitted` : 'Submitted'} submitted forecasts in every single round of the challenge!\n\nTrack ${botName ? 'its' : 'my'} profile:`,
	},
};

export function getAchievementMetadata(
	type: UserAchievementsType,
	categoryId?: string | null
): AchievementMetadata | undefined {
	const base = ACHIEVEMENT_METADATA[type];
	if (!base) return undefined;

	const override = categoryId
		? ACHIEVEMENT_CATEGORY_METADATA_OVERRIDES[categoryId as GameCategory]?.[type]
		: undefined;
	const iconSlug = resolveIconSlug(type);

	return { ...base, ...(iconSlug ? { iconSlug } : {}), ...override } as AchievementMetadata;
}
